import { Checkbox } from '../../../../components/checkbox/checkbox';
import { Field, FieldDescription, FieldLabel } from '../../../../components/field/field';
import {
  PurchaseOrderProductLineItem,
  UpsertPurchaseOrderProductInventoryUnit,
} from '../../../../clients/quipli-service/purchase-order/purchase-order';
import { Typography } from '../../../../components/typography/typography';

type ProductUnitFormProps = {
  purchaseOrderProduct: PurchaseOrderProductLineItem;
  units: UpsertPurchaseOrderProductInventoryUnit[];
  onUpdate: (units: UpsertPurchaseOrderProductInventoryUnit[]) => void;
  index: number;
  markFieldAsTouched: (fieldName: string) => void;
  isFieldTouched: (fieldName: string) => boolean;
  errors?: Record<string, string>;
};

export const ProductUnitForm = ({
  index,
  purchaseOrderProduct,
  units,
  onUpdate,
  markFieldAsTouched,
  isFieldTouched,
  errors = {},
}: ProductUnitFormProps) => {
  // Get the unit
  const unit = units[index];

  // Update the unit
  const updateUnit = (u: UpsertPurchaseOrderProductInventoryUnit) => {
    onUpdate(
      units.map((o, i) => {
        if (i === index) {
          return u;
        } else {
          return o;
        }
      }),
    );
  };

  // Check if fields are touched
  const isUnitNumberTouched = isFieldTouched('unitNumber');
  const isSerialNumberTouched = isFieldTouched('serialNumber');
  const isSaleRentTouched = isFieldTouched('forSale') || isFieldTouched('forRent');

  // Get specific error messages
  const unitNumberError = errors['unitNumber'];
  const serialNumberError = errors['serialNumber'];
  const saleRentError = errors['saleRent'];

  return (
    <div className="flex flex-column gap-2">
      <div className="flex gap-4 items-start justify-between mb-2">
        <div className="flex gap-2 flex-wrap">
          <Typography type="text-md" weight="medium" className="text-grey-700">
            Product:
          </Typography>
          <Typography type="text-md" className="text-quipli-black-500">
            {purchaseOrderProduct.product.name}
          </Typography>
        </div>
        <div className="flex gap-2 shrink-0">
          <Typography type="text-md" weight="medium" className="text-grey-700">
            Unit:
          </Typography>
          <Typography type="text-md" className="text-quipli-black-500" dataCy="product-unit-form-total-units">
            {index + 1} of {purchaseOrderProduct.quantity}
          </Typography>
        </div>
      </div>

      {/* Fields */}
      <Field
        isRequired
        type="text"
        label="Unit Number"
        dataCy="product-unit-form-unit-number"
        value={unit.unitNumber}
        onChange={(v) => {
          markFieldAsTouched('unitNumber');
          updateUnit({ ...unit, unitNumber: v });
        }}
        description={isUnitNumberTouched && unitNumberError ? unitNumberError : undefined}
        invalid={isUnitNumberTouched && !!unitNumberError}
      />
      <Field
        isRequired
        type="text"
        label="Serial Number"
        dataCy="product-unit-form-serial-number"
        value={unit.serialNumber}
        onChange={(v) => {
          markFieldAsTouched('serialNumber');
          updateUnit({ ...unit, serialNumber: v });
        }}
        description={isSerialNumberTouched && serialNumberError ? serialNumberError : undefined}
        invalid={isSerialNumberTouched && !!serialNumberError}
      />
      <Field
        type="text"
        label="QR Code"
        dataCy="product-unit-form-qr-code"
        value={unit.qrCode || ''}
        onChange={(v) => {
          markFieldAsTouched('qrCode');
          updateUnit({ ...unit, qrCode: v });
        }}
      />
      <Field
        type="number"
        label="Machine Hours"
        dataCy="product-unit-form-machine-hours"
        value={unit.hourReading || ''}
        onChange={(v) => {
          markFieldAsTouched('hourReading');
          updateUnit({ ...unit, hourReading: parseFloat(v) });
        }}
      />
      {purchaseOrderProduct.product.allowSale && purchaseOrderProduct.product.allowRent && (
        <div className="mt-1">
          <FieldLabel label="For Sale/Rent" isRequired />
          <div className="flex flex-col gap-2">
            <Checkbox
              label="Sale"
              dataCy="product-unit-form-for-sale"
              isChecked={unit.forSale}
              onChange={() => {
                markFieldAsTouched('forSale');
                updateUnit({ ...unit, forSale: !unit.forSale });
              }}
            />
            <Checkbox
              label="Rent"
              dataCy="product-unit-form-for-rent"
              isChecked={unit.forRent}
              onChange={() => {
                markFieldAsTouched('forRent');
                updateUnit({ ...unit, forRent: !unit.forRent });
              }}
            />
          </div>
        </div>
      )}

      {isSaleRentTouched && saleRentError && <FieldDescription description={saleRentError} invalid />}
    </div>
  );
};
